import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Mail, RefreshCw, MessageSquare, Trash2, CheckCircle2, Clock3, MessageCircle } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusBadgeClass(status) {
  const normalized = String(status || "pending").toLowerCase();
  if (normalized === "resolved" || normalized === "closed") {
    return "border-emerald-500/35 bg-emerald-500/10 text-emerald-300";
  }
  if (normalized === "in_progress") {
    return "border-blue-500/35 bg-blue-500/10 text-blue-300";
  }
  return "border-amber-500/35 bg-amber-500/10 text-amber-300";
}

function normalizeWhatsAppNumber(phone) {
  const digits = String(phone || "").replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.startsWith("62")) return digits;
  if (digits.startsWith("0")) return `62${digits.slice(1)}`;
  return digits;
}

export default function ContactUs() {
  const [activeTab, setActiveTab] = useState("contact");
  const [isLoading, setIsLoading] = useState(false);
  const [contactRows, setContactRows] = useState([]);
  const [newsletterRows, setNewsletterRows] = useState([]);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [contactRes, newsletterRes] = await Promise.all([
        axios.get(`${API}/contact-submissions`, { withCredentials: true }),
        axios.get(`${API}/newsletter-subscriptions`, { withCredentials: true }),
      ]);
      setContactRows(Array.isArray(contactRes.data) ? contactRes.data : []);
      setNewsletterRows(Array.isArray(newsletterRes.data) ? newsletterRes.data : []);
    } catch (error) {
      console.error("Failed to fetch contact/newsletter data:", error);
      alert(error.response?.data?.detail || "Gagal memuat data Contact Us");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredContacts = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return contactRows;
    return contactRows.filter((row) =>
      [row.name, row.email, row.phone, row.message, row.status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword))
    );
  }, [contactRows, search]);

  const filteredNewsletters = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return newsletterRows;
    return newsletterRows.filter((row) => String(row.email || "").toLowerCase().includes(keyword));
  }, [newsletterRows, search]);

  const handleUpdateStatus = async (submissionId, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/contact-submissions/${submissionId}/status`,
        { status },
        { withCredentials: true }
      );
      setContactRows((prev) => prev.map((row) => (row.id === submissionId ? { ...row, ...(data || {}) } : row)));
    } catch (error) {
      console.error("Failed to update submission status:", error);
      alert(error.response?.data?.detail || "Gagal update status pesan");
    }
  };

  const handleDeleteContact = async (submissionId) => {
    if (!window.confirm("Hapus pesan ini?")) return;
    try {
      await axios.delete(`${API}/contact-submissions/${submissionId}`, { withCredentials: true });
      setContactRows((prev) => prev.filter((row) => row.id !== submissionId));
    } catch (error) {
      console.error("Failed to delete contact submission:", error);
      alert(error.response?.data?.detail || "Gagal hapus pesan");
    }
  };

  const handleDeleteNewsletter = async (subscriptionId) => {
    if (!window.confirm("Hapus subscriber ini?")) return;
    try {
      await axios.delete(`${API}/newsletter-subscriptions/${subscriptionId}`, { withCredentials: true });
      setNewsletterRows((prev) => prev.filter((row) => row.id !== subscriptionId));
    } catch (error) {
      console.error("Failed to delete newsletter subscription:", error);
      alert(error.response?.data?.detail || "Gagal hapus subscriber");
    }
  };

  const pendingCount = contactRows.filter((row) => String(row.status || "pending").toLowerCase() === "pending").length;
  const resolvedCount = contactRows.filter((row) => ["resolved", "closed"].includes(String(row.status || "").toLowerCase())).length;

  const handleReplyWhatsApp = (row) => {
    const phone = normalizeWhatsAppNumber(row.phone);
    if (!phone) {
      alert("Nomor WhatsApp pengirim tidak tersedia atau tidak valid.");
      return;
    }
    const greetingName = row.name || "Kak";
    const customerMessage = String(row.message || "").trim();
    const text = encodeURIComponent(
      `Halo ${greetingName},\n\n` +
        `Terima kasih sudah menghubungi *FitPulse Pro*.\n` +
        `Pesan Anda sudah kami terima dan tim kami siap membantu.\n\n` +
        `*Ringkasan pesan Anda:*\n` +
        `"${customerMessage || "-"}"\n\n` +
        `Agar kami bisa bantu lebih cepat, silakan tambahkan detail yang dibutuhkan.\n` +
        `Terima kasih.`
    );
    window.open(`https://wa.me/${phone}?text=${text}`, "_blank", "noopener,noreferrer");
  };

  return (
    <DashboardLayout>
      <Header title="Contact Us" />
      <div className="p-8 space-y-6" data-testid="contact-us-page">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-sm border border-white/10 bg-zinc-900/50 p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Total Pesan</p>
            <p className="mt-2 text-3xl font-bold text-white">{contactRows.length}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-zinc-900/50 p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Pending</p>
            <p className="mt-2 text-3xl font-bold text-amber-300">{pendingCount}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-zinc-900/50 p-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Resolved</p>
            <p className="mt-2 text-3xl font-bold text-emerald-300">{resolvedCount}</p>
          </div>
        </div>

        <div className="rounded-sm border border-white/10 bg-zinc-900/50">
          <div className="flex flex-col gap-3 border-b border-white/10 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("contact")}
                className={`rounded-sm px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition-colors ${
                  activeTab === "contact" ? "bg-[#D4AF37] text-black" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                }`}
                data-testid="tab-contact"
              >
                Contact Inbox
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("newsletter")}
                className={`rounded-sm px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition-colors ${
                  activeTab === "newsletter" ? "bg-[#D4AF37] text-black" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                }`}
                data-testid="tab-newsletter"
              >
                Newsletter
              </button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={activeTab === "contact" ? "Cari nama/email/pesan..." : "Cari email..."}
                className="bg-zinc-900 border-white/10 text-white rounded-sm"
                data-testid="contact-search-input"
              />
              <Button
                type="button"
                onClick={fetchData}
                disabled={isLoading}
                className="rounded-sm border border-white/10 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"
                data-testid="refresh-contact-btn"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>

          {activeTab === "contact" ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-white/[0.03] text-zinc-400">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Pengirim</th>
                    <th className="px-4 py-3 text-left font-medium">Pesan</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Waktu</th>
                    <th className="px-4 py-3 text-right font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
                        Tidak ada pesan Contact Us.
                      </td>
                    </tr>
                  ) : (
                    filteredContacts.map((row) => (
                      <tr key={row.id} className="border-t border-white/5 align-top">
                        <td className="px-4 py-4">
                          <p className="font-semibold text-white">{row.name || "-"}</p>
                          <p className="mt-1 text-xs text-zinc-400">{row.email || "-"}</p>
                          <p className="mt-1 text-xs text-zinc-500">{row.phone || "-"}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="max-w-[520px] whitespace-pre-wrap leading-relaxed text-zinc-200">{row.message || "-"}</p>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex rounded-sm border px-2 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${statusBadgeClass(row.status)}`}>
                            {String(row.status || "pending").replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-zinc-400">{formatDate(row.created_at)}</td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleReplyWhatsApp(row)}
                              className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/35 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-500/20"
                              data-testid={`contact-whatsapp-btn-${row.id}`}
                            >
                              <MessageCircle className="h-3.5 w-3.5" />
                              WhatsApp
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateStatus(row.id, "in_progress")}
                              className="inline-flex items-center gap-1 rounded-sm border border-blue-500/35 bg-blue-500/10 px-2 py-1 text-xs text-blue-300 hover:bg-blue-500/20"
                              data-testid={`contact-progress-btn-${row.id}`}
                            >
                              <Clock3 className="h-3.5 w-3.5" />
                              Progress
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateStatus(row.id, "resolved")}
                              className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/35 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-500/20"
                              data-testid={`contact-resolve-btn-${row.id}`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Resolve
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteContact(row.id)}
                              className="inline-flex items-center gap-1 rounded-sm border border-red-500/35 bg-red-500/10 px-2 py-1 text-xs text-red-300 hover:bg-red-500/20"
                              data-testid={`contact-delete-btn-${row.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Hapus
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-white/[0.03] text-zinc-400">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Email</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Subscribed At</th>
                    <th className="px-4 py-3 text-right font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNewsletters.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-zinc-500">
                        Tidak ada subscriber newsletter.
                      </td>
                    </tr>
                  ) : (
                    filteredNewsletters.map((row) => (
                      <tr key={row.id} className="border-t border-white/5">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2 text-white">
                            <Mail className="h-4 w-4 text-[#D4AF37]" />
                            {row.email || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="inline-flex rounded-sm border border-emerald-500/35 bg-emerald-500/10 px-2 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-emerald-300">
                            {row.status || "active"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-zinc-400">{formatDate(row.subscribed_at)}</td>
                        <td className="px-4 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleDeleteNewsletter(row.id)}
                            className="inline-flex items-center gap-1 rounded-sm border border-red-500/35 bg-red-500/10 px-2 py-1 text-xs text-red-300 hover:bg-red-500/20"
                            data-testid={`newsletter-delete-btn-${row.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Hapus
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-sm border border-white/10 bg-zinc-900/40 p-4 text-sm text-zinc-400">
          <div className="flex items-start gap-2">
            <MessageSquare className="mt-0.5 h-4 w-4 text-[#D4AF37]" />
            <p>
              Inbox ini terhubung langsung dengan form Contact Us dan Newsletter di website client. Tim admin bisa follow-up pesan, update status, dan membersihkan data lama dari sini.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

